import { create } from 'zustand';

/**
 * Favorites / quick-order list. A rep's curated set of items they sell often —
 * the B2B "order guide" pattern. Persisted per device; surfaced on Home and
 * toggled from the item screen. Stores enough to add straight to the cart.
 */
export interface FavoriteItem {
  spireId: number;
  partNo: string;
  description: string | null;
  whseCode: string;
  /** Decimal as string — the item's default sell price at save time. */
  price: string;
  uom: string | null;
}

interface FavoritesState {
  items: FavoriteItem[];
  has: (spireId: number) => boolean;
  toggle: (item: FavoriteItem) => void;
  remove: (spireId: number) => void;
}

const KEY = 'kdental:favorites';
const MAX = 200;

function load(): FavoriteItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as FavoriteItem[]) : [];
  } catch {
    return [];
  }
}
function persist(items: FavoriteItem[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* private mode — ignore */
  }
}

export const useFavorites = create<FavoritesState>((set, get) => ({
  items: load(),
  has: (spireId) => get().items.some((i) => i.spireId === spireId),
  toggle: (item) => {
    const exists = get().items.some((i) => i.spireId === item.spireId);
    const items = exists
      ? get().items.filter((i) => i.spireId !== item.spireId)
      : [item, ...get().items].slice(0, MAX);
    set({ items });
    persist(items);
  },
  remove: (spireId) => {
    const items = get().items.filter((i) => i.spireId !== spireId);
    set({ items });
    persist(items);
  },
}));
